export type EmbeddedBrowserInfo = {
  isEmbedded: boolean;
  reason: string;
};

export type BrowserPlatform = "iOS" | "Android" | "Desktop" | "Unknown";
export type BrowserName =
  | "Safari"
  | "Chrome"
  | "Firefox"
  | "Edge"
  | "Opera"
  | "Samsung Internet"
  | "DuckDuckGo"
  | "WebView"
  | "Unknown";
export type BrowserContext = "native" | "embedded";

export type BrowserFingerprint = {
  platform: BrowserPlatform;
  browser: BrowserName;
  context: BrowserContext;
  wrapperApp: string | null;
  reason: string;
  isMobile: boolean;
  userAgent: string;
};

const KNOWN_IN_APP_SIGNATURES = [
  {
    app: "Facebook / Messenger",
    reason: "facebook-in-app-browser",
    pattern: /FBAN|FBAV|FBMS|FB_IAB|FB4A|FBAN\/Messenger|Messenger/i,
  },
  {
    app: "Instagram",
    reason: "instagram-in-app-browser",
    pattern: /Instagram/i,
  },
  {
    app: "WhatsApp",
    reason: "whatsapp-in-app-browser",
    pattern: /WhatsApp/i,
  },
  {
    app: "Line",
    reason: "line-in-app-browser",
    pattern: /Line\//i,
  },
  {
    app: "WeChat",
    reason: "wechat-in-app-browser",
    pattern: /MicroMessenger/i,
  },
  {
    app: "Telegram",
    reason: "telegram-in-app-browser",
    pattern: /Telegram/i,
  },
  {
    app: "TikTok",
    reason: "tiktok-in-app-browser",
    pattern: /TikTok/i,
  },
  {
    app: "Snapchat",
    reason: "snapchat-in-app-browser",
    pattern: /Snapchat/i,
  },
  {
    app: "Google App",
    reason: "google-app-in-app-browser",
    pattern: /\bGSA\/|GoogleApp/i,
  },
  {
    app: "QR Scanner App",
    reason: "qr-scanner-webview",
    pattern: /qr[\s_-]?scanner|barcode[\s_-]?scanner|scanbot|zxing|qrreader/i,
  },
];

const KNOWN_IOS_STANDALONE_BROWSERS =
  /CriOS|FxiOS|EdgiOS|OPiOS|YaBrowser|DuckDuckGo/i;

const KNOWN_ANDROID_STANDALONE_BROWSERS =
  /Chrome|Firefox|SamsungBrowser|EdgA|Edg\/|OPR|Opera|DuckDuckGo/i;

function detectPlatform(userAgent: string): BrowserPlatform {
  if (/iPhone|iPad|iPod/i.test(userAgent)) return "iOS";
  if (/Android/i.test(userAgent)) return "Android";
  if (/Macintosh|Windows NT|Linux|CrOS/i.test(userAgent)) return "Desktop";
  return "Unknown";
}

function detectBrowserName(userAgent: string, platform: BrowserPlatform): BrowserName {
  if (/SamsungBrowser/i.test(userAgent)) return "Samsung Internet";
  if (/EdgiOS|EdgA|Edg\//i.test(userAgent)) return "Edge";
  if (/OPiOS|OPR\/|Opera/i.test(userAgent)) return "Opera";
  if (/FxiOS|Firefox/i.test(userAgent)) return "Firefox";
  if (/DuckDuckGo/i.test(userAgent)) return "DuckDuckGo";
  if (/CriOS|Chrome/i.test(userAgent)) return "Chrome";

  if (platform === "iOS" && /Version\/[\d.]+.*Safari/i.test(userAgent)) {
    return "Safari";
  }

  if (/; wv\)|\bwv\b|WebView/i.test(userAgent)) {
    return "WebView";
  }

  return "Unknown";
}

export function classifyUserAgent(userAgent: string): BrowserFingerprint {
  const platform = detectPlatform(userAgent);
  const browser = detectBrowserName(userAgent, platform);
  const isMobile = /Android|iPhone|iPad|iPod/i.test(userAgent);

  const knownWrapperMatch = KNOWN_IN_APP_SIGNATURES.find(({ pattern }) =>
    pattern.test(userAgent)
  );

  const isIOSWebViewSignature =
    platform === "iOS" &&
    /AppleWebKit/i.test(userAgent) &&
    !KNOWN_IOS_STANDALONE_BROWSERS.test(userAgent) &&
    !/Safari/i.test(userAgent);

  const isAndroidWebViewSignature =
    platform === "Android" &&
    (/; wv\)|\bwv\b|WebView/i.test(userAgent) ||
      (/Version\/[\d.]+/i.test(userAgent) &&
        /Chrome\/[\d.]+/i.test(userAgent) &&
        !KNOWN_ANDROID_STANDALONE_BROWSERS.test(userAgent)));

  const isEmbedded =
    Boolean(knownWrapperMatch) ||
    isIOSWebViewSignature ||
    isAndroidWebViewSignature;

  let reason = "none";
  if (knownWrapperMatch) {
    reason = knownWrapperMatch.reason;
  } else if (isIOSWebViewSignature) {
    reason = "ios-webview-signature";
  } else if (isAndroidWebViewSignature) {
    reason = "android-webview-signature";
  }

  return {
    platform,
    browser: isEmbedded && browser === "Unknown" ? "WebView" : browser,
    context: isEmbedded ? "embedded" : "native",
    wrapperApp: knownWrapperMatch?.app ?? null,
    reason,
    isMobile,
    userAgent,
  };
}

export function detectEmbeddedBrowser(userAgent: string): EmbeddedBrowserInfo {
  if (!userAgent) {
    return { isEmbedded: false, reason: "missing-user-agent" };
  }
  const result = classifyUserAgent(userAgent);
  return {
    isEmbedded: result.context === "embedded",
    reason: result.reason,
  };
}

/**
 * Keep single-part uploads for detected in-app browsers and WebViews.
 * Standalone mobile browsers use the same size-based multipart policy as desktop.
 * User-agent checks cannot detect an in-app browser that impersonates Safari.
 */
export function shouldDisableMultipartUploads(userAgent: string): boolean {
  const result = classifyUserAgent(userAgent);
  const isExplicitIOSWebView = result.platform === "iOS" && /\bwv\b|WebView/i.test(userAgent);
  return result.context === "embedded" || isExplicitIOSWebView;
}
